// src/components/Price/PriceGrid.jsx

'use client';

import { usePreferences } from '@/contexts/ShopPreferences.context';
import { currencies } from '@/lib/currencies.utils';
import { Money } from '@/lib/money.utils';
import { createFormatters } from '@/lib/formatters.utils';
import styles from './PriceGrid.module.scss';


export default function PriceGrid({
  pricing,
  locale,
  priceType = 'current',
  startText = null,
  endText = null,
  fontSize = null,
  fontWeight = '400',
  isCrossed = false,
  style
}) {

  // Get currency from context
  const { currency } = usePreferences();

  // Figure out which price to use
  let priceValue = 0;

  if (pricing) {
    // First try to use the specified priceType
    // If not available, fall back to current, then original
    const selectedPriceType = pricing[priceType] ? priceType :
                             (pricing.current ? 'current' : 'original');

    // Get the price in the current currency or base currency
    const priceStr = pricing[selectedPriceType]?.[currency] ||
                     pricing[selectedPriceType]?.[pricing.baseCurrency] ||
                     '0';

    // Create a Money object and get its numeric value
    try {
      const moneyObj = new Money(priceStr, currency);
      priceValue = moneyObj.toNumber();
    } catch (error) {
      console.error('Error converting price to number:', error);
      priceValue = 0;
    }
  } else {
    // No pricing provided
    return null
  }

  // Use the currency formatter from formatters.utils.js
  const { currencyFormatter } = createFormatters(locale);

  // Get the fully formatted price for accessibility
  const formattedPrice = currencyFormatter(priceValue, currency);

  // We still need to get the parts for styling
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: currencies[currency]?.decimalPlaces || 2,
    maximumFractionDigits: currencies[currency]?.decimalPlaces || 2
  });

  // Split the formatted price at the decimal separator
  const parts = formatter.formatToParts(priceValue);

  let integerPart = '';
  let fractionPart = '';
  let decimalSeparator = '.';
  let currencySymbol = '';

  // Extract integer, fraction, decimal separator, and currency symbol
  parts.forEach(part => {
    if (part.type === 'integer' || part.type === 'group') {
      integerPart += part.value;
    } else if (part.type === 'fraction') {
      fractionPart = part.value;
    } else if (part.type === 'decimal') {
      decimalSeparator = part.value;
    } else if (part.type === 'currency') {
      currencySymbol = part.value.trim();
    }
  });

  // Only show a fraction if the currency has decimal places and the fraction is not all zeros
  const decimalPlaces = currencies[currency]?.decimalPlaces || 0;
  const showFraction = decimalPlaces > 0 && Boolean(Number.parseInt(fractionPart));

  const intPart = <span className={styles.int}>{integerPart}</span>;

  return (
    <span className={styles.priceWrapper} style={{ fontSize, fontWeight, ...style }} aria-label={formattedPrice}>
      {startText && <span className={styles.priceText}>{startText}</span>}

      <span className={styles.price}>
        <span className={styles.symbol}>{currencySymbol || currencies[currency]?.symbol || '$'}</span>
        {isCrossed ? <del>{intPart}</del> : intPart}
        {showFraction && (
          <span className={styles.frac}>
            <span className={styles.sep}>{decimalSeparator}</span>{fractionPart}
          </span>
        )}
      </span>

      {endText && <span className={styles.priceText}>{endText}</span>}
    </span>
  );
}
